import { authStore } from '@/lib/authStore';
import { resolveApiUrl } from '@/lib/apiBase';

/**
 * Single-flight token refresh.
 *
 * Calls POST /auth/refresh with credentials: 'include' to rotate the
 * session cookie. On success, updates authStore with the new expiry.
 * Concurrent calls share the same in-flight promise.
 */

let refreshPromise: Promise<boolean> | null = null;

async function doRefresh(): Promise<boolean> {
  try {
    const url = resolveApiUrl('/auth/refresh');
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'include',
    });

    if (!res.ok) {
      return false;
    }

    const body = await res.json();
    if (!body.ok) {
      return false;
    }

    // Update authStore expiry with the new TTL from the server
    const expiresInMs =
      typeof body.expiresIn === 'number' ? body.expiresIn * 1000 : undefined;
    const currentToken = authStore.getToken();
    const currentUser = authStore.getUser();
    authStore.setAuth(currentToken, undefined, currentUser, expiresInMs);

    return true;
  } catch {
    return false;
  }
}

export async function attemptTokenRefresh(): Promise<boolean> {
  // Single-flight: share the in-flight promise
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = doRefresh().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

/** Exposed for testing only. */
export function _resetForTest(): void {
  refreshPromise = null;
}
